import { useCallback, useState } from 'react'
import type { IslandProps } from '../registry'
import { useSession } from '../IslandRoot'
import { Button, EmptyState, SearchInput } from '../../shared/ui'
import { PipelineBoard } from './PipelineBoard'
import { usePipeline } from './usePipeline'
import { computeStats, formatMoney } from './pipelineMath'

/**
 * Deals screen (legacy `#panel-pipeline`). Owns the stats bar, search,
 * kanban board and stage moves. The add / edit modal and the deal drawer
 * stay legacy for now: this island opens them through the host's
 * `openAddDeal(stage)` and `openDealDrawer(id)`.
 */

type HostWindow = Window & {
  openAddDeal?: (stage?: string) => void
  openDealDrawer?: (id: string) => void
}

/**
 * Phones: the five stats wrap to two columns (the last one full width)
 * instead of being clipped. The board keeps its own horizontal scroll.
 */
const PIPELINE_CSS = `
@media (max-width: 639px) {
  .pipeline-stats.stats-bar.cols5 { grid-template-columns: repeat(2, 1fr); }
  .pipeline-stats .stat-cell { padding: 14px 16px; }
  .pipeline-stats .stat-cell:last-child { grid-column: 1 / -1; }
}
`

function openAddDeal(stage?: string) {
  const fn = (window as HostWindow).openAddDeal
  if (typeof fn !== 'function') return
  if (stage) fn(stage)
  else fn()
}

function openDealDrawer(id: string) {
  ;(window as HostWindow).openDealDrawer?.(id)
}

export default function PipelineIsland({ user }: IslandProps) {
  const session = useSession()
  const userId = session.user?.id ?? user?.id ?? window.user?.id ?? null
  const { deals, status, error, refetch, moveDeal } = usePipeline(userId)
  const [search, setSearch] = useState('')
  const onSearch = useCallback((v: string) => setSearch(v), [])
  const onMove = useCallback((id: string, stage: string) => void moveDeal(id, stage), [moveDeal])

  const stats = computeStats(deals)
  const ready = status === 'success'

  return (
    <>
      <style>{PIPELINE_CSS}</style>
      {/* A row of zeros on an empty board says nothing; the empty state does. */}
      {!(ready && deals.length === 0) && (
        <div className="stats-bar cols5 pipeline-stats" style={{ marginBottom: 20 }}>
          <Stat label="Total deals" value={ready ? String(stats.total) : '—'} />
          <Stat
            label="Pipeline value"
            tone="amber"
            value={ready ? formatMoney(stats.pipelineValue) : '—'}
          />
          <Stat label="Avg deal size" value={ready ? formatMoney(stats.avgDealSize) : '—'} />
          <Stat label="Won" tone="green" value={ready ? String(stats.won) : '—'} />
          <Stat label="Close rate" value={ready ? `${stats.closeRate}%` : '—'} />
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 16 }}>
        <SearchInput value={search} onChange={onSearch} placeholder="Search deals…" />
        <Button onClick={() => openAddDeal()}>+ Deal</Button>
      </div>

      {(status === 'loading' || status === 'idle') && (
        <div
          role="status"
          style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 13 }}
        >
          Loading deals…
        </div>
      )}

      {status === 'error' && (
        <EmptyState
          role="alert"
          title="Couldn't load deals."
          message={error ?? 'Something went wrong.'}
          cta={{ label: 'Try again', onClick: refetch }}
        />
      )}

      {ready && deals.length === 0 && (
        <EmptyState
          title="No deals yet."
          message="Add a deal to track it from Prospect to Closed Won. Drag cards between stages as they move."
          cta={{ label: '+ Add a deal', onClick: () => openAddDeal() }}
        />
      )}

      {ready && deals.length > 0 && (
        <PipelineBoard
          deals={deals}
          search={search}
          onMove={onMove}
          onOpenDeal={openDealDrawer}
          onAddDeal={openAddDeal}
        />
      )}
    </>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'amber' | 'green' }) {
  return (
    <div className="stat-cell">
      <div className="stat-label">{label}</div>
      <div className={tone ? `stat-value ${tone}` : 'stat-value'}>{value}</div>
    </div>
  )
}
